import { Skeleton } from "@tripcart/traveler-ui/components/skeleton";
import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./_calendar-skeleton.module.css";

function NavButtonSkeleton() {
  return <div className={styles.navButton} />;
}

function MonthLabelSkeleton() {
  return <Skeleton variant="title" className={styles.monthLabel} />;
}

/** One month grid (weekday header + 6 × 7 day cells). The caption row
 *  isn't part of this — react-day-picker puts the nav chevrons at the
 *  outermost edges across both months, not on every month, so the
 *  outer `CalendarSkeleton` owns the caption layout. */
function CalendarMonthBodySkeleton() {
  return (
    <div className={styles.body}>
      {/* Weekday header — 7 × 52 px cells, text placeholder centered. */}
      <div className={styles.row}>
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className={styles.weekdayCell}>
            <div className={styles.weekdayBar} />
          </div>
        ))}
      </div>
      {/* 6 weeks × 7 days — `fixedWeeks` on the live calendar always
          renders 6 rows, so we do too. Each cell is a 52 px square with
          a rounded pulsing fill mirroring the real day buttons. */}
      <div className={styles.weeks}>
        {Array.from({ length: 6 }).map((_, week) => (
          <div key={week} className={styles.row}>
            {Array.from({ length: 7 }).map((_, day) => (
              <div key={day} className={styles.dayCell}>
                <Skeleton variant="tile" className={styles.dayFill} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton fallback rendered while the lazy calendar chunk
 *  (`react-day-picker` + `date-fns`, ~50 KB gzipped) is in flight. Mirrors
 *  the real calendar 1:1 — same padding, same 40 px circular nav buttons,
 *  same 52 px day cells with rounded corners, same gap between the two
 *  desktop months, same caption + weekday header rows — so the popover
 *  reveals into the final layout without a size jump. Mobile collapses to
 *  one month to match the live calendar's responsive `numberOfMonths`. */
export function CalendarSkeleton() {
  return (
    <div className={styles.root}>
      <div className={styles.months}>
        {/* First month — prev chevron sits at its outer-left edge; the
            month label takes the remaining space and right-aligns
            against the gap that separates the two months. On mobile
            (single month) the next chevron joins this row at the
            outer-right edge. */}
        <div className={styles.month}>
          <div className={styles.caption}>
            <NavButtonSkeleton />
            <MonthLabelSkeleton />
            {/* Mobile-only next chevron — desktop hides this because
                the next chevron lives on the second month's caption. */}
            <div className={styles.mobileOnly}>
              <NavButtonSkeleton />
            </div>
            {/* Desktop spacer to keep the label centered between the
                outer prev button and the gap. */}
            <div className={cx(styles.spacer, styles.desktopSpacer)} />
          </div>
          <CalendarMonthBodySkeleton />
        </div>
        {/* Second month — desktop only. Month label on the left, next
            chevron at the outer-right edge. */}
        <div className={cx(styles.month, styles.secondMonth)}>
          <div className={styles.caption}>
            <div className={styles.spacer} />
            <MonthLabelSkeleton />
            <NavButtonSkeleton />
          </div>
          <CalendarMonthBodySkeleton />
        </div>
      </div>
    </div>
  );
}
